/** @jsx jsx */
// eslint-disable-next-line
import React, { Component } from 'react'
import { css, jsx } from '@emotion/core'
import { withStyles } from '@material-ui/core'
import MuiDialogContent from '@material-ui/core/DialogContent'
import Checkbox from '@material-ui/core/Checkbox'
import Button from '@material-ui/core/Button'
import PropTypes from 'prop-types'
import { theme } from '../../constants'
import { ProductNames } from '../../constants/payments'
import formatPrice from '../../utils/formatPrice'
import localizePriceByCountry from '../../utils/localizePriceByCountry'
import localizePriceByCurrency from '../../utils/localizePriceByCurrency'
import localizeCurrency from '../../utils/localizeCurrency'
import GameCloudLogo from '../../assets/game-cloud-logo.png'
import PlayBaseLogo from '../../assets/play-base-logo.svg'
import NexusLogo from '../../assets/nexus-logo.svg'

const styles = {}
styles.container = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  fontSize: '14px',
  border: '1px solid #cecece',
  borderRadius: '8px',
  boxShadow: '0px 8px 16px -16px #767676',
  padding: '16px',
  paddingRight: '32px',
  marginBottom: '16px',
  color: 'white'
}

styles.title = css`
  font-weight: bold;
  margin-bottom: 8px;
  margin-top: 0;
  font-size: 20px;
  margin-bottom: 16px;
`
styles.productLogo = {
  height: '40px',
  marginBottom: '10px'
}

styles.description = css``
styles.featuresContainer = css`
  display: flex;
  flex-direction: column;
  padding-left: 10px;
  font-size: 16px;
`

styles.featureDescription = css``
styles.subFeaturesIndent = css`
  padding-left: 32px;
  margin-top: 4px;
  margin-bottom: 4px;
`
styles.addonsContainer = css`
  display: flex;
  flex-direction: column;
  margin-top: 12px;
`
styles.addonTitle = css`
  font-size: 16px;
  margin-bottom: 8px;
`

styles.addonPrice = css`
  min-width: 100px;
  text-align: right;
`
styles.addonContainer = css`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
`
styles.addonToggleContainer = css`
  display: flex;
  align-items: center;
`
styles.totalContainer = css`
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
`
styles.removeButton = css`
  cursor: pointer;
  color: grey;
  :hover {
    text-decoration: underline;
  }
`
styles.productPrice = css`
  font-size: 20px;
  font-weight: bold;
`

styles.cycle = css`
  font-size: 14px;
`

styles.planQuantityContainer = css`
  position: absolute;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  top: 16px;
  right: 16px;
`
styles.packQuantityContainer = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`
styles.quantity = css`
  margin: 0px 12px;
  user-select: none;
  font-size: 20px;
  font-weight: bold;
`
styles.quantityButton = {
  width: '30px',
  height: '30px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  border: `1px solid ${theme.palette.common.charolBlack}`,
  background: `${theme.palette.primary.main}`,
  cursor: 'pointer',
  userSelect: 'none',
  fontWeight: 'bold',
  fontSize: '30px'
}

styles.plusMinusIcon = {
  marginTop: '-9px'
}

styles.disabled = css`
  pointer-events: none;
  opacity: 0.5;
`

styles.addonPrice = {
  fontSize: '16px',
  fontWeight: 'bold'
}

styles.addonCycle = {
  fontSize: '10px'
}

const DialogContent = withStyles(() => ({
  root: {
    flex: 'unset !important',
    padding: '0px !important',
    '& .MuiSwitch-colorPrimary.Mui-checked.Mui-disabled + .MuiSwitch-track': {
      backgroundColor: theme.palette.primary.main,
      opacity: 0.5
    },
    '& .MuiSwitch-colorPrimary.Mui-checked + .MuiSwitch-track': {
      backgroundColor: theme.palette.primary.main,
      opacity: 1
    },
    '& .MuiSwitch-thumb': {
      color: theme.palette.common.white
    }
  }
}))(MuiDialogContent)

const AddOns2026 = {
  playbase_essentials: [],
  playbase_plus: [],
  playbase_advanced: [],
  nexus_prime: [{ id: 'nexus_prime_live', translationKey: 'nexus_prime.addon1' }],
  nexus_pro: [{ id: 'nexus_pro_live', translationKey: 'nexus_pro.addon1' }],
  nexus_elite: []
}

const AddOns2024 = {
  starter: [
    { id: 'starter_pause', translationKey: 'starter.addon1' },
    { id: 'starter_code', translationKey: 'starter.addon2' }
  ],
  essentials: [
    { id: 'essentials_code', translationKey: 'essentials.addon1' },
    { id: 'essentials_mft', translationKey: 'essentials.addon2' }
  ],
  prime: [
    { id: 'prime_mft', translationKey: 'prime.addon1' },
    { id: 'prime_presets', translationKey: 'prime.addon2' },
    { id: 'prime_live', translationKey: 'prime.addon3' }
  ],
  pro: [{ id: 'pro_live', translationKey: 'pro.addon1' }],
  elite: []
}

const AddOns2023 = [
  { id: 'codepad', previousId: '', value: 'codepad', translationKey: 'code' },
  { id: 'basic', previousId: '', value: 'unlimited', translationKey: 'basic' },
  {
    id: 'basic_mft_codepad',
    previousId: 'basic_codepad',
    value: 'mft',
    translationKey: 'fullBasic'
  },
  { id: 'lite', value: 'automatic', previousId: 'basic_mft_codepad', translationKey: 'lite' }
]

function RecurrentProduct(props) {
  const productInfo = props.product
    ? ProductNames.find(v => v.value === props.product.productCode)
    : null
  const label = window.I18n.t('plans.plan', {
    planName: productInfo
      ? productInfo.translationKey
        ? window.I18n.t(`plans.${productInfo.translationKey}`)
        : productInfo.translation
      : 'Starter'
  })

  const isNexus = props.product && props.product.productCode.includes('nexus')
  const isGameCloud = props.product && props.product.productCode.includes('minutes')

  const Descriptions2024 = {
    starter_pause: window.I18n.t('products.starter.starterPauseDescription'),
    starter_code: window.I18n.t('products.starter.starterCodeDescription'),
    starter_code_pause: window.I18n.t('products.starter.starterCodePauseDescription'),
    essentials: window.I18n.t('products.essentials.essentialsDescription'),
    essentials_code: window.I18n.t('products.essentials.essentialsCodeDescription'),
    essentials_mft: window.I18n.t('products.essentials.essentialsMftDescription'),
    essentials_mft_code: window.I18n.t('products.essentials.essentialsMftCodeDescription'),
    prime: window.I18n.t('products.prime.primeDescription'),
    prime_mft: window.I18n.t('products.prime.primeMftDescription'),
    prime_presets: window.I18n.t('products.prime.primePresetsDescription'),
    prime_live: window.I18n.t('products.prime.primeDescription'),
    prime_presets_mft: window.I18n.t('products.prime.primePresetsMftDescription'),
    prime_presets_live: window.I18n.t('products.prime.primePresetsDescription'),
    prime_mft_live: window.I18n.t('products.prime.primeMftDescription'),
    prime_presets_mft_live: window.I18n.t('products.prime.primePresetsMftDescription'),
    pro_2024: window.I18n.t('products.pro.description'),
    pro_2024_live: window.I18n.t('products.pro.description'),
    elite_2024: window.I18n.t('products.elite.description'),
    // Deprecated plans.
    lite: window.I18n.t('products.liteDescription')
  }

  const Descriptions = {
    playbase_essentials: window.I18n.t('products.playbase_essentials.description'),
    playbase_plus: window.I18n.t('products.playbase_plus.description'),
    playbase_advanced: window.I18n.t('products.playbase_advanced.description'),
    nexus_prime: window.I18n.t('products.nexus_prime.description'),
    nexus_prime_live: window.I18n.t('products.nexus_prime.description'),
    nexus_pro: window.I18n.t('products.nexus_pro.description'),
    nexus_pro_live: window.I18n.t('products.nexus_pro.description'),
    nexus_elite: window.I18n.t('products.nexus_elite.description')
  }

  const Features = {
    free: [
      window.I18n.t('products.freeFeature1'),
      window.I18n.t('products.freeFeature2'),
      window.I18n.t('products.freeFeature3'),
      window.I18n.t('products.freeFeature4')
    ],
    playbase_essentials: [
      window.I18n.t('products.playbase_essentials.feature1'),
      window.I18n.t('products.playbase_essentials.feature2')
    ],
    playbase_plus: [
      window.I18n.t('products.playbase_plus.feature1'),
      window.I18n.t('products.playbase_plus.feature2'),
      window.I18n.t('products.playbase_plus.feature3')
    ],
    playbase_advanced: [
      window.I18n.t('products.playbase_advanced.feature1'),
      window.I18n.t('products.playbase_advanced.feature2'),
      window.I18n.t('products.playbase_advanced.feature3')
    ],
    nexus_prime: [
      window.I18n.t('products.nexus_prime.feature1'),
      window.I18n.t('products.nexus_prime.feature2'),
      window.I18n.t('products.nexus_prime.feature3')
    ],
    nexus_pro: [
      window.I18n.t('products.nexus_pro.feature1'),
      window.I18n.t('products.nexus_pro.feature2'),
      window.I18n.t('products.nexus_pro.feature3')
    ],
    nexus_elite: [
      window.I18n.t('products.nexus_elite.feature1'),
      window.I18n.t('products.nexus_elite.feature2'),
      window.I18n.t('products.nexus_elite.feature3')
    ],

    starter: [
      window.I18n.t('products.starter.feature1'),
      window.I18n.t('products.starter.feature2'),
      window.I18n.t('products.starter.feature3')
    ],
    essentials: [
      window.I18n.t('products.essentials.feature1'),
      window.I18n.t('products.essentials.feature2'),
      window.I18n.t('products.essentials.feature3')
    ],
    prime: [
      window.I18n.t('products.prime.feature1'),
      window.I18n.t('products.prime.feature2'),
      [
        window.I18n.t('products.prime.feature2_1'),
        window.I18n.t('products.prime.feature2_2'),
        window.I18n.t('products.prime.feature2_3')
      ]
    ],
    pro: [
      window.I18n.t('products.pro.feature1'),
      window.I18n.t('products.pro.feature2'),
      window.I18n.t('products.pro.feature3'),
      window.I18n.t('products.pro.feature4'),
      [
        window.I18n.t('products.pro.feature4_1'),
        window.I18n.t('products.pro.feature4_2'),
        window.I18n.t('products.pro.feature4_3'),
        window.I18n.t('products.pro.feature4_4')
      ]
    ],
    elite: [
      window.I18n.t('products.elite.feature1'),
      window.I18n.t('products.elite.feature2'),
      window.I18n.t('products.elite.feature3'),
      window.I18n.t('products.elite.feature4')
    ]
  }

  let keyAddOns = 'free'
  if (props.product) {
    if (!props.product.isDeprecated) {
      if (props.product.productCode.includes('playbase_essentials')) {
        keyAddOns = 'playbase_essentials'
      } else if (props.product.productCode.includes('playbase_plus')) {
        keyAddOns = 'playbase_plus'
      } else if (props.product.productCode.includes('playbase_advanced')) {
        keyAddOns = 'playbase_advanced'
      } else if (props.product.productCode.includes('nexus_prime')) {
        keyAddOns = 'nexus_prime'
      } else if (props.product.productCode.includes('nexus_pro')) {
        keyAddOns = 'nexus_pro'
      } else if (props.product.productCode.includes('nexus_elite')) {
        keyAddOns = 'nexus_elite'
      }
    } else {
      // Must be checked before 'elite', which would also match the deprecated Nexus Elite.
      if (props.product.productCode.includes('nexus_elite')) {
        keyAddOns = 'nexus_elite'
      } else if (props.product.productCode.includes('starter')) {
        keyAddOns = 'starter'
      } else if (props.product.productCode.includes('essentials')) {
        keyAddOns = 'essentials'
      } else if (props.product.productCode.includes('prime')) {
        keyAddOns = 'prime'
      } else if (props.product.productCode.includes('pro')) {
        keyAddOns = 'pro'
      } else if (props.product.productCode.includes('elite')) {
        keyAddOns = 'elite'
      }
    }
  }

  const features = Features[keyAddOns]

  let logo = ''
  let accentColor = theme.palette.primary.main
  let contrastColor = 'black'

  if (isNexus) {
    styles.container.background = 'var(--nexus-blue-black-gradient)'
    styles.container.borderColor = 'var(--nexus-blue)'
    styles.quantityButton.background = 'var(--nexus-blue)'
    accentColor = 'var(--nexus-blue)'
    contrastColor = 'white'

    logo = NexusLogo
  } else if (isGameCloud) {
    styles.container.background = 'var(--game-cloud-purple-black-gradient)'
    styles.container.borderColor = 'var(--game-cloud-purple)'
    styles.quantityButton.background = 'var(--game-cloud-purple)'
    accentColor = 'var(--game-cloud-purple)'
    contrastColor = 'white'

    logo = GameCloudLogo
  } else {
    styles.container.background = 'var(--play-base-green-black-gradient)'
    styles.container.borderColor = 'var(--play-base-green)'
    styles.quantityButton.background = 'var(--play-base-green)'
    styles.quantityButton.color = theme.palette.common.charolBlack
    accentColor = 'var(--play-base-green)'
    contrastColor = 'black'

    logo = PlayBaseLogo
  }

  let canBeUpgraded = false
  const upgradablePlanParts = [
    'playbase_essentials',
    'playbase_plus',
    'playbase_advanced',
    'nexus_prime',
    'nexus_pro'
  ]
  const planParts = [
    'playbase_essentials',
    'playbase_plus',
    'playbase_advanced',
    'nexus_prime',
    'nexus_pro',
    'nexus_elite'
  ]

  let nextPlan = null
  let nextPlanTranslation = null

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < upgradablePlanParts.length; i++) {
    if (!props.product || props.product.productCode.includes(upgradablePlanParts[i])) {
      canBeUpgraded = true
      nextPlan =
        planParts[planParts.findIndex(v => v === upgradablePlanParts[i]) + (props.product ? 1 : 0)]

      // eslint-disable-next-line no-loop-func
      nextPlanTranslation = ProductNames.find(v => v.value === nextPlan).translation
      break
    }
  }

  let upgradeBackgroundColor
  let upgradeTextColor
  if (canBeUpgraded) {
    if (nextPlan.includes('nexus')) {
      upgradeBackgroundColor = 'var(--nexus-blue)'
      upgradeTextColor = 'white'
    } else {
      upgradeBackgroundColor = 'var(--play-base-green)'
      upgradeTextColor = 'black'
    }
  }

  return (
    <DialogContent>
      <div css={styles.container}>
        <div>
          <img css={styles.productLogo} src={logo} alt="logo" />
          <p css={styles.title}>{label}</p>
        </div>
        <div css={styles.featuresContainer}>
          {features.map(v => {
            return (
              <span css={styles.featureDescription}>
                {Array.isArray(v) ? (
                  <ul css={styles.subFeaturesIndent}>
                    {v.map(vv => (
                      <li>{vv}</li>
                    ))}
                  </ul>
                ) : (
                  v
                )}
              </span>
            )
          })}
        </div>
        {props.product && !props.product.isDeprecated && !props.product.isPrivate && (
          <div css={styles.addonsContainer}>
            {AddOns2026[keyAddOns].map(v => {
              const products =
                props.cycle === 'monthly'
                  ? props.monthlyProducts
                  : props.cycle === 'yearly'
                    ? props.yearlyProducts
                    : props.halfYearlyProducts
              const addonPrice = 0

              let isChecked = false
              if (keyAddOns === 'nexus_prime') {
                if (v.id === 'nexus_prime_live') {
                  isChecked = props.product.productCode === 'nexus_prime_live'
                }
              } else if (keyAddOns === 'nexus_pro') {
                if (v.id === 'nexus_pro_live') {
                  isChecked = props.product.productCode === 'nexus_pro_live'
                }
              }

              return (
                <div key={`addon-${v.id}`} css={styles.addonContainer}>
                  <div css={styles.addonToggleContainer}>
                    <Checkbox
                      style={{
                        marginRight: '3px',
                        padding: 0,
                        color: accentColor,
                        '&$checked': {
                          color: accentColor
                        }
                      }}
                      name={v.id}
                      checked={isChecked}
                      color="primary"
                      onChange={() => {
                        let newProductId

                        switch (props.product.productCode) {
                          case 'nexus_prime':
                            if (v.id === 'nexus_prime_live') {
                              newProductId = 'nexus_prime_live'
                            }
                            break

                          case 'nexus_prime_live':
                            if (v.id === 'nexus_prime_live') {
                              newProductId = 'nexus_prime'
                            }
                            break

                          case 'nexus_pro':
                            if (v.id === 'nexus_pro_live') {
                              newProductId = 'nexus_pro_live'
                            }
                            break

                          case 'nexus_pro_live':
                            if (v.id === 'nexus_pro_live') {
                              newProductId = 'nexus_pro'
                            }
                            break

                          default:
                        }

                        props.onUpdate([
                          { ...props.product, quantity: 0 },
                          {
                            ...products.find(p => p.productCode === newProductId),
                            quantity: props.product.quantity
                          }
                        ])
                      }}
                    />
                    <span>{window.I18n.t(`products.${v.translationKey}`)}</span>
                  </div>
                  {addonPrice > 0 && (
                    <span
                      css={css`
                        ${styles.addonPrice}
                        ${isChecked ? '' : styles.disabled}
                      `}
                    >
                      {props.currencyCode} {formatPrice(addonPrice, props.currencyCode)}{' '}
                      {props.cycle === 'monthly'
                        ? window.I18n.t('checkout.perMonth')
                        : props.cycle === 'yearly'
                          ? window.I18n.t('checkout.perYear')
                          : window.I18n.t('checkout.perHalfYear')}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        )}
        {props.product && props.product.isDeprecated && props.product.businessModel === 2024 && (
          <div css={styles.addonsContainer}>
            {AddOns2024[keyAddOns].length > 0 && (
              <span css={styles.addonTitle}>{window.I18n.t('products.addOns')}</span>
            )}
            {AddOns2024[keyAddOns].map(v => {
              const addonPrice = 0.0

              let isChecked = false
              if (keyAddOns === 'starter') {
                if (v.id === 'starter_pause') {
                  isChecked =
                    props.product.productCode === 'starter_pause' ||
                    props.product.productCode === 'starter_code_pause'
                } else if (v.id === 'starter_code') {
                  isChecked =
                    props.product.productCode === 'starter_code' ||
                    props.product.productCode === 'starter_code_pause'
                }
              } else if (keyAddOns === 'essentials') {
                if (v.id === 'essentials_code') {
                  isChecked =
                    props.product.productCode === 'essentials_code' ||
                    props.product.productCode === 'essentials_mft_code'
                } else if (v.id === 'essentials_mft') {
                  isChecked =
                    props.product.productCode === 'essentials_mft' ||
                    props.product.productCode === 'essentials_mft_code'
                }
              } else if (keyAddOns === 'prime') {
                if (v.id === 'prime_mft') {
                  isChecked =
                    props.product.productCode === 'prime_mft' ||
                    props.product.productCode === 'prime_presets_mft' ||
                    props.product.productCode === 'prime_mft_live' ||
                    props.product.productCode === 'prime_presets_mft_live'
                } else if (v.id === 'prime_presets') {
                  isChecked =
                    props.product.productCode === 'prime_presets' ||
                    props.product.productCode === 'prime_presets_mft' ||
                    props.product.productCode === 'prime_presets_live' ||
                    props.product.productCode === 'prime_presets_mft_live'
                } else if (v.id === 'prime_live') {
                  isChecked =
                    props.product.productCode === 'prime_live' ||
                    props.product.productCode === 'prime_presets_live' ||
                    props.product.productCode === 'prime_mft_live' ||
                    props.product.productCode === 'prime_presets_mft_live'
                }
              } else if (keyAddOns === 'pro') {
                if (v.id === 'pro_live') {
                  isChecked = props.product.productCode === 'pro_2024_live'
                }
              }

              // Deprecated plans have everything disabled.
              const isDisabled = true

              return (
                <div key={`addon-${v.id}`} css={styles.addonContainer}>
                  <div css={styles.addonToggleContainer}>
                    <Checkbox
                      style={{
                        marginRight: '3px',
                        padding: 0,
                        color: accentColor,
                        '&$checked': {
                          color: accentColor
                        }
                      }}
                      name={v.id}
                      checked={isChecked}
                      color="primary"
                      disabled={isDisabled}
                    />
                    <span css={styles.addonDescription}>
                      {window.I18n.t(`products.${v.translationKey}`)}
                    </span>
                  </div>
                  {addonPrice > 0 && (
                    <div
                      css={css`
                        ${isChecked ? '' : styles.disabled}
                      `}
                    >
                      <span css={styles.addonPrice}>{props.currencyCode}</span>
                      <span> </span>
                      <span css={styles.addonPrice}>
                        {formatPrice(addonPrice, props.currencyCode)}
                      </span>
                      <span css={styles.addonCycle}>
                        {props.cycle === 'monthly'
                          ? window.I18n.t('checkout.perMonth')
                          : props.cycle === 'yearly'
                            ? window.I18n.t('checkout.perYear')
                            : window.I18n.t('checkout.perHalfYear')}
                      </span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
        {props.product && props.product.isDeprecated && props.product.businessModel === 2023 && (
          <div css={styles.addonsContainer}>
            <span css={styles.addonTitle}>{window.I18n.t('products.addOns')}</span>
            {AddOns2023.map(v => {
              const products =
                props.cycle === 'monthly'
                  ? props.monthlyProducts
                  : props.cycle === 'yearly'
                    ? props.yearlyProducts
                    : props.halfYearlyProducts
              const product = products.find(p => p.productCode === v.id)
              if (!product) {
                return null
              }

              const previousProduct = v.previousId
                ? products.find(p => p.productCode === v.previousId)
                : null
              if (!previousProduct) {
                return null
              }

              const productPrice = props.isShopping
                ? localizePriceByCountry(product, props.currencyCode, props.countryCode)
                : localizePriceByCurrency(product, props.currencyCode)
              const addonPrice = previousProduct
                ? productPrice -
                  (props.isShopping
                    ? localizePriceByCountry(previousProduct, props.currencyCode, props.countryCode)
                    : localizePriceByCurrency(previousProduct, props.currencyCode))
                : productPrice

              let isChecked
              if (v.value === 'codepad') {
                isChecked =
                  props.product.productCode !== 'basic' && props.product.productCode !== 'basic_mft'
              } else if (v.value === 'unlimited') {
                isChecked = props.product.productCode !== 'codepad'
              } else if (v.value === 'mft') {
                isChecked = ['basic_mft', 'basic_mft_codepad', 'lite'].includes(
                  props.product.productCode
                )
              } else if (v.value === 'automatic') {
                isChecked = props.product.productCode === 'lite'
              }

              // Deprecated plans have everything disabled.
              const isDisabled = true

              return (
                <div key={`addon-${v.id}`} css={styles.addonContainer}>
                  <div css={styles.addonToggleContainer}>
                    <Checkbox
                      style={{
                        marginRight: '3px',
                        padding: 0,
                        color: accentColor,
                        '&$checked': {
                          color: accentColor
                        }
                      }}
                      name={v.id}
                      checked={isChecked}
                      color="primary"
                      disabled={isDisabled}
                    />
                    <span css={styles.addonDescription}>
                      {window.I18n.t(`products.${v.translationKey}`)}
                    </span>
                  </div>
                  {addonPrice > 0 && (
                    <div
                      css={css`
                        ${isChecked ? '' : styles.disabled}
                      `}
                    >
                      <span css={styles.addonPrice}>{props.currencyCode}</span>
                      <span> </span>
                      <span css={styles.addonPrice}>
                        {formatPrice(addonPrice, props.currencyCode)}
                      </span>
                      <span css={styles.addonCycle}>
                        {props.cycle === 'monthly'
                          ? window.I18n.t('checkout.perMonth')
                          : props.cycle === 'yearly'
                            ? window.I18n.t('checkout.perYear')
                            : window.I18n.t('checkout.perHalfYear')}
                      </span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
        {canBeUpgraded && (
          <div>
            <Button
              style={{
                borderRadius: '8px',
                marginTop: '16px',
                padding: '6px 24px',
                color: upgradeTextColor,
                backgroundColor: upgradeBackgroundColor,
                textTransform: 'none'
              }}
              onClick={() => {
                const products =
                  props.cycle === 'monthly'
                    ? props.monthlyProducts
                    : props.cycle === 'yearly'
                      ? props.yearlyProducts
                      : props.halfYearlyProducts
                const nextProduct = products.find(p => p.productCode === nextPlan)
                if (nextProduct) {
                  if (props.product) {
                    props.onUpgrade(
                      { ...nextProduct, quantity: props.product.quantity },
                      { ...props.product, quantity: 0 }
                    )
                  } else {
                    props.onUpgrade({ ...nextProduct, quantity: 1 })
                  }

                  return
                }

                console.error('Product not found')
              }}
            >
              {window.I18n.t('checkout.upgradeTo', { plan: nextPlanTranslation })}
            </Button>
          </div>
        )}
        {props.product && (
          <div css={styles.planQuantityContainer}>
            <div
              css={css`
                ${styles.quantityButton}
                ${props.product.isDeprecated || props.product.quantity <= 1 ? styles.disabled : ''}
              `}
              onClick={() => {
                props.onUpdate([
                  { ...props.product, quantity: Math.max(0, props.product.quantity - 1) }
                ])
              }}
            >
              <span css={{ ...styles.plusMinusIcon, color: contrastColor }}>-</span>
            </div>
            <span css={styles.quantity}>{props.product.quantity}</span>
            <div
              css={css`
                ${styles.quantityButton}
                ${props.product.isDeprecated || props.product.quantity >= 99 ? styles.disabled : ''}
              `}
              onClick={() => {
                props.onUpdate([
                  { ...props.product, quantity: Math.min(99, props.product.quantity + 1) }
                ])
              }}
            >
              <span css={{ ...styles.plusMinusIcon, color: contrastColor }}>+</span>
            </div>
          </div>
        )}
        {props.product ? (
          <div css={styles.totalContainer}>
            {props.showRemove ? (
              <a
                css={styles.removeButton}
                onClick={() => props.onUpdate([{ ...props.product, quantity: 0 }])}
              >
                {window.I18n.t('products.remove')}
              </a>
            ) : (
              <span> </span>
            )}

            <span css={styles.totalDescription}>
              <span css={styles.productPrice}>
                {props.isShopping
                  ? localizeCurrency(props.product, props.countryCode)
                  : props.currencyCode}
              </span>
              <span> </span>
              <span css={styles.productPrice}>
                {formatPrice(
                  (props.isShopping
                    ? localizePriceByCountry(props.product, props.currencyCode, props.countryCode)
                    : localizePriceByCurrency(props.product, props.currencyCode)) *
                    props.product.quantity,
                  props.currencyCode
                )}
              </span>
              <span css={styles.cycle}>
                {props.cycle === 'monthly'
                  ? window.I18n.t('checkout.perMonth')
                  : props.cycle === 'yearly'
                    ? window.I18n.t('checkout.perYear')
                    : window.I18n.t('checkout.perHalfYear')}
              </span>
            </span>
          </div>
        ) : (
          <div css={styles.totalContainer}>
            <span> </span>
            <span css={styles.totalDescription}>
              <span css={styles.productPrice}>{props.currencyCode}</span>
              <span> </span>
              <span css={styles.productPrice}>{formatPrice(0, props.currencyCode)}</span>
              <span css={styles.cycle}>
                {props.cycle === 'monthly'
                  ? window.I18n.t('checkout.perMonth')
                  : props.cycle === 'yearly'
                    ? window.I18n.t('checkout.perYear')
                    : window.I18n.t('checkout.perHalfYear')}
              </span>
            </span>
          </div>
        )}
      </div>
    </DialogContent>
  )
}

RecurrentProduct.defaultProps = {
  countryCode: '',
  product: undefined,
  monthlyProducts: undefined,
  halfYearlyProducts: undefined,
  yearlyProducts: undefined,
  isShopping: true,
  showRemove: true,
  onUpdate: () => {},
  onUpgrade: () => {},
  productType: 'playbase'
}

RecurrentProduct.propTypes = {
  currencyCode: PropTypes.string.isRequired,
  countryCode: PropTypes.string,
  product: PropTypes.object,
  monthlyProducts: PropTypes.array,
  halfYearlyProducts: PropTypes.array,
  yearlyProducts: PropTypes.array,
  isShopping: PropTypes.bool,
  cycle: PropTypes.string.isRequired,
  showRemove: PropTypes.bool,
  onUpgrade: PropTypes.func,
  onUpdate: PropTypes.func,
  productType: 'playbase' | 'nexus' | 'minutes'
}

export default RecurrentProduct
